import * as React from 'react'
import {Box, Typography} from '@mui/material'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, withOpacity} from '../../../../styles/foundation/colors'
import {RADIUS, GAP} from '../../../../styles/foundation/spacing'
import {EVENT_KIND} from '../data/reference'
import {date} from '../format'
import {groupRounds, EVENT_TONE} from './logic'
import {toneColor, chipSx} from '../components/tone'
import RqSection from './RqSection'

/**
 * Icon per event KIND, never per prose. An eighth kind a future backend adds
 * falls back to `CircleOutlinedIcon` + the 'neutral' tone (see the `|| `
 * fallbacks below) instead of crashing or rendering `undefined`.
 */
const EVENT_ICON = {
  [EVENT_KIND.RAISED]: NoteAddOutlinedIcon,
  [EVENT_KIND.SUBMITTED]: UploadOutlinedIcon,
  [EVENT_KIND.INFO_REQUESTED]: HelpOutlineIcon,
  [EVENT_KIND.NOTE]: ChatBubbleOutlineIcon,
  [EVENT_KIND.APPROVED]: CheckCircleOutlineIcon,
  [EVENT_KIND.DECLINED]: CancelOutlinedIcon,
  [EVENT_KIND.LIMIT_REACHED]: HourglassEmptyIcon,
  [EVENT_KIND.WITHDRAWN]: CancelOutlinedIcon,
  [EVENT_KIND.SUPERSEDED]: HourglassEmptyIcon,
  [EVENT_KIND.PROPOSED]: NoteAddOutlinedIcon,
  [EVENT_KIND.PROPOSAL_ACCEPTED]: CheckCircleOutlineIcon,
  [EVENT_KIND.PROPOSAL_DECLINED]: CancelOutlinedIcon,
}

/**
 * One event row: icon-in-circle + connector on the left, the action on the
 * right, and who/when in a column of its own from `sm` up. Date and actor
 * used to be a subline under the action, which left every short entry —
 * "Freeze request raised." — trailing dead width to the card edge. As a
 * right-hand column they fill that width instead and become scannable down
 * the list.
 */
function EventRow({event, last}) {
  const Icon = EVENT_ICON[event.kind] || CircleOutlinedIcon
  const tone = EVENT_TONE[event.kind] || 'neutral'
  const color = toneColor(tone)

  return (
    <Box component='li' sx={{display: 'grid', gridTemplateColumns: '20px minmax(0,1fr)', columnGap: GAP.sm}}>
      <Box sx={{position: 'relative', display: 'flex', justifyContent: 'center'}}>
        {!last && (
          <Box aria-hidden='true' sx={{position: 'absolute', top: 20, bottom: -2, width: '1px', bgcolor: BORDER}} />
        )}
        <Box
          aria-hidden='true'
          sx={{
            width: 20, height: 20, flex: '0 0 20px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: withOpacity(color, 0.14), color,
          }}
        >
          <Icon sx={{fontSize: 12}} />
        </Box>
      </Box>

      <Box
        sx={{
          pb: GAP.sm, minWidth: 0, display: 'grid', columnGap: GAP.sm,
          gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) auto'},
          alignItems: 'baseline',
        }}
      >
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, lineHeight: 1.5, gridColumn: 1, gridRow: 1}}>
          {event.text}
        </Typography>

        {/* The whole point of the redesign: the mockup recovered this by
            stripping an "Additional data requested:" prefix off the sentence.
            We just read the array the event already carries. */}
        {Array.isArray(event.requestedItems) && event.requestedItems.length > 0 && (
          <Box sx={{display: 'flex', gap: GAP.xs, flexWrap: 'wrap', mt: GAP.xs, gridColumn: 1, alignSelf: 'start'}}>
            {event.requestedItems.map((item, i) => (
              <Box
                key={item + i}
                component='span'
                sx={{...chipSx('info'), px: 0.75, borderRadius: RADIUS.sm, display: 'inline-flex', alignItems: 'center'}}
              >
                {item}
              </Box>
            ))}
          </Box>
        )}

        {/* Last in the DOM so the action and what it asked for are read
            together; pulled up into the right-hand column from `sm`. */}
        <Typography sx={{
          fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.5,
          gridColumn: {xs: 1, sm: 2}, gridRow: {sm: 1},
          textAlign: {sm: 'right'}, whiteSpace: {sm: 'nowrap'},
        }}>
          {date(event.date)} · {event.actor}
        </Typography>
      </Box>
    </Box>
  )
}

/** "ROUND n" pill, uppercase and tabular, sitting on a neutral border tint. */
function RoundPill({round}) {
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex', alignItems: 'center', px: 0.75, py: 0.15,
        borderRadius: RADIUS.sm, bgcolor: BORDER, color: SUBTLE,
        fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'],
        textTransform: 'uppercase', letterSpacing: '0.04em', fontVariantNumeric: 'tabular-nums',
      }}
    >
      Round {round}
    </Box>
  )
}

/**
 * Nested timeline: round groups, each containing a connected dot-and-line
 * event list. Two genuine nesting levels, which is exactly what `@mui/lab`'s
 * `Timeline` (used elsewhere in this codebase, e.g.
 * `disputes-v2/DisputeDrawer.js`) does not support — it is opinionated about
 * a single flat left/right-alternating list. Hand-rolled instead: a plain
 * `<ol>` of round `<li>`s, each optionally containing its own `<ol>` of
 * event `<li>`s on CSS Grid. No `@mui/lab` import here.
 *
 * `history`/`round` come straight off the request; `groupRounds` (logic.js)
 * is what turns them into contiguous round groups 1..N, including any round
 * with nothing recorded in it yet.
 *
 * `label` is the heading, not just an `aria-label`. All three consumers used
 * to render that string themselves AND hand the same words down for the
 * list's accessible name; RqSection renders it once and names the block with
 * it, so the words exist in exactly one place per surface.
 */
export default function RqTimeline({history, round, label = 'Review timeline'}) {
  const groups = groupRounds(history, round)
  if (!groups.length) return null

  return (
    <RqSection title={label}>
      <Box component='ol' sx={{display: 'grid', gap: GAP.md, listStyle: 'none', m: 0, p: 0}}>
        {groups.map((group) => (
          <Box component='li' key={group.round} sx={{display: 'grid', gap: GAP.xs}}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: GAP.sm, flexWrap: 'wrap',
              py: 0.35, px: 0.75, borderRadius: RADIUS.sm, bgcolor: withOpacity(toneColor(group.tone), 0.08),
            }}>
              <RoundPill round={group.round} />
              <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: toneColor(group.tone)}}>
                {group.outcome}
              </Typography>
            </Box>

            {group.events.length === 0 ? (
              <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, pl: '32px'}}>
                Nothing has been recorded in this round yet.
              </Typography>
            ) : (
              <Box component='ol' sx={{display: 'grid', rowGap: 0.5, listStyle: 'none', m: 0, p: 0}}>
                {group.events.map((event, i) => (
                  <EventRow key={event.date + event.kind + i} event={event} last={i === group.events.length - 1} />
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </RqSection>
  )
}
